import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { ARQUETIPS, arquetipPerId, capAlLlenc } from '../arquetips';
import { detectaBucles } from '../buclesCausals';
import { LLENC, POLARITAT } from '../tema';
import { createModel, saveCausalLoop } from '../store/modelSlice';
import { textRic } from '../textRic';

// LA BIBLIOTECA D'ARQUETIPS.
//
// ── PER QUÈ TÉ LLOC PROPI I NO ÉS UN BOTÓ DEL LLENÇ CAUSAL ──────────────────────────────────
//
// Perquè **un arquetip no és una plantilla**. Una plantilla és un punt de partida per dibuixar;
// un arquetip és **una forma que has de saber reconèixer**, i porta tres coses que un dibuix sol
// no pot dir: la història, el parany i el punt de palanca.
//
// Un desplegable d'una barra d'eines no pot portar això —i el llenç causal ja va just d'espai—.
// Aquí es fullegen i es llegeixen, i des d'aquí **s'obren com un model de bucles causals** ja
// dibuixat, que es pot fer córrer i adaptar al cas de cadascú.
//
// **Els arquetips en si no viuen aquí**: són a `src/arquetips.js`, sense imports i amb proves
// pròpies que comproven que les polaritats dels bucles són les que el text diu.

// El dibuix d'un arquetip. No és el llenç: és una miniatura per reconèixer la forma. Per això
// no hi ha ni zoom, ni ratolí, ni res per tocar.
function Miniatura({ arquetip, nomDe, alt = 340 }) {
  const R = 30;
  const vs = arquetip.variables;
  const per = Object.fromEntries(vs.map((x) => [x.id, x]));
  const x0 = Math.min(...vs.map((x) => x.x)) - 90;
  const y0 = Math.min(...vs.map((x) => x.y)) - 60;
  const x1 = Math.max(...vs.map((x) => x.x)) + 110;
  const y1 = Math.max(...vs.map((x) => x.y)) + 70;

  // Les relacions es corben, com al llenç causal: A→B i B→A han de quedar a bandes diferents o
  // se superposen i el bucle no es veu.
  const corba = (o, d) => {
    const dx = d.x - o.x, dy = d.y - o.y;
    const dist = Math.hypot(dx, dy) || 1;
    const desv = Math.min(48, dist * 0.2);
    const c = { x: (o.x + d.x) / 2 - (dy / dist) * desv, y: (o.y + d.y) / 2 + (dx / dist) * desv };
    const anc = (p, cap) => {
      const ux = (cap.x - p.x) / (Math.hypot(cap.x - p.x, cap.y - p.y) || 1);
      const uy = (cap.y - p.y) / (Math.hypot(cap.x - p.x, cap.y - p.y) || 1);
      return { x: p.x + ux * R, y: p.y + uy * R };
    };
    const p0 = anc(o, c), p1 = anc(d, c);
    const t = 0.92;
    const abans = {
      x: (1 - t) ** 2 * p0.x + 2 * (1 - t) * t * c.x + t ** 2 * p1.x,
      y: (1 - t) ** 2 * p0.y + 2 * (1 - t) * t * c.y + t ** 2 * p1.y,
    };
    return { d: `M ${p0.x} ${p0.y} Q ${c.x} ${c.y} ${p1.x} ${p1.y}`, p0, p1, c,
             angle: Math.atan2(p1.y - abans.y, p1.x - abans.x) * 180 / Math.PI };
  };

  return (
    <svg viewBox={`${x0} ${y0} ${x1 - x0} ${y1 - y0}`} className="w-full" style={{ maxHeight: alt }}
      role="img" aria-label={nomDe(`arq.${arquetip.id}Nom`)}>
      {arquetip.relacions.map((rel, i) => {
        const o = per[rel.from], d = per[rel.to];
        if (!o || !d) return null;
        const g = corba(o, d);
        const color = rel.polarity === '-' ? POLARITAT.negativa : POLARITAT.positiva;
        const mig = { x: 0.25 * g.p0.x + 0.5 * g.c.x + 0.25 * g.p1.x,
                      y: 0.25 * g.p0.y + 0.5 * g.c.y + 0.25 * g.p1.y };
        return (
          <g key={i}>
            <path d={g.d} fill="none" stroke={color} strokeWidth="2" />
            <polygon points="0,0 -9,-4 -9,4" fill={color}
              transform={`translate(${g.p1.x},${g.p1.y}) rotate(${g.angle})`} />
            <circle cx={mig.x} cy={mig.y} r="8" fill="#FFFFFF" />
            <text x={mig.x} y={mig.y + 4} textAnchor="middle" fontSize="13" fontWeight="700" fill={color}>
              {rel.polarity === '-' ? '−' : '+'}
            </text>
            {/* El retard, amb la marca de la disciplina: dues ratlles travessades a la fletxa. */}
            {rel.delay > 0 && (
              <g transform={`translate(${(g.p0.x + mig.x) / 2},${(g.p0.y + mig.y) / 2}) rotate(${g.angle})`}>
                <line x1="-3" y1="-7" x2="-3" y2="7" stroke={LLENC.text} strokeWidth="2" />
                <line x1="3" y1="-7" x2="3" y2="7" stroke={LLENC.text} strokeWidth="2" />
              </g>
            )}
          </g>
        );
      })}
      {vs.map((x) => (
        <g key={x.id}>
          <circle cx={x.x} cy={x.y} r={R} fill={LLENC.variable.farciment}
            stroke={LLENC.variable.vora} strokeWidth="2" />
          <text x={x.x} y={x.y + R + 15} textAnchor="middle" fontSize="12" fill={LLENC.text}
            stroke="#FFFFFF" strokeWidth="3" paintOrder="stroke">
            {nomDe(x.clau)}
          </text>
        </g>
      ))}
    </svg>
  );
}

const comptaBucles = (arq) => {
  const bucles = detectaBucles(
    arq.variables.map((x) => ({ id: x.id })),
    arq.relacions.map((x, i) => ({ id: `r${i}`, source: x.from, target: x.to, polarity: x.polarity })),
  );
  return { r: bucles.filter((b) => b.type === 'reinforcing').length,
           b: bucles.filter((b) => b.type === 'balancing').length };
};

export default function Arquetips() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // D'ON VENIES. Els arquetips s'obren des del llenç causal, i **no hi havia manera de tornar-hi**:
  // no és una pàgina que es visiti per si mateixa, és material que consultes enmig d'una feina.
  // L'enllaç del llenç hi posa `state.tornaA`; qui hi arribi de cap altra manera va a Models, que
  // és on hi ha la seva feina. `navigate(-1)` no serviria: si has arribat aquí de fora,
  // et tregui de l'aplicació.
  const location = useLocation();
  // D'ON VENIES, I NOMÉS SI ÉS D'AQUÍ DINS.
  //
  // `react-router` 6.30.6 —l'última de la sèrie— **encara arrossega un open redirect** que deriva
  // en XSS; l'arranjament només és a la 7, que és un salt major i toca la navegació sencera. En
  // comptes de fer-lo ara, es tanca el vector pel nostre costat: aquest és **l'únic lloc de
  // l'aplicació on una destinació de navegació ve d'una dada i no d'un literal**.
  //
  // La regla és que ha de ser un camí intern: comença per una barra i **no per dues** ni per
  // `/\`, que és com es fabrica un `//exemple.com` que el navegador llegeix com un domini. Avui
  // el valor el posa el nostre propi codi i no viatja per l'URL, o sigui que no és explotable —
  // però és exactament la forma que aquesta vulnerabilitat demana, i el dia que algú hi passi un
  // valor de fora, aquesta línia ja hi és.
  const proposat = location.state?.tornaA;
  const intern = typeof proposat === 'string'
    && /^\/(?![/\\])/.test(proposat);
  const tornaA = intern ? proposat : '/models';
  const [obert, setObert] = useState(null);
  const [creant, setCreant] = useState(false);
  const arq = arquetipPerId(obert);

  // OBRIR-LO COM A MODEL. Es crea un model de bucles causals i s'hi desa el dibuix de seguida:
  // així el que s'obre ja està desat i no es perd si es tanca la pestanya.
  const obreComAModel = async () => {
    if (!arq || creant) return;
    setCreant(true);
    try {
      const model = await dispatch(createModel({
        name: t('arq.obrintNom', { nom: t(`arq.${arq.id}Nom`) }),
        description: t(`arq.${arq.id}Ganxo`),
        type: 'causal_loop',
      })).unwrap();
      const id = model?.id || model?.model?.id;
      if (!id) throw new Error('sense id');
      const dibuix = capAlLlenc(arq, t, Date.now());
      await dispatch(saveCausalLoop({ modelId: id, ...dibuix })).unwrap();
      navigate(`/models/${id}`);
    } catch (e) {
      setCreant(false);
    }
  };

  if (arq) {
    const n = comptaBucles(arq);
    return (
      <div className="max-w-4xl mx-auto">
        <button type="button" onClick={() => setObert(null)}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeftIcon className="h-4 w-4" />{t('arq.tornar')}
        </button>
        <Link to={tornaA}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4 -mt-2">
          <ArrowLeftIcon className="h-4 w-4" />
          {t(intern ? 'arq.tornaAlLlenc' : 'arq.tornaAModels')}
        </Link>

        <header className="mb-5">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">{t(`arq.${arq.id}Nom`)}</h1>
            <span className="text-xs font-medium text-gray-500 tabular-nums">
              {t('arq.bucles', n)}
            </span>
          </div>
          <p className="text-gray-600 mt-1">{t(`arq.${arq.id}Ganxo`)}</p>
        </header>

        <div className="bg-white border border-gray-200 rounded-md p-4 mb-5">
          <Miniatura arquetip={arq} nomDe={t} />
        </div>

        <div className="space-y-4">
          <section className="bg-white border border-gray-200 rounded-md p-5">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {t('arq.laHistoria')}
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed"
               dangerouslySetInnerHTML={{ __html: textRic(t(`arq.${arq.id}Historia`)) }} />
          </section>

          <section className="bg-warn-50 border border-warn-400 rounded-md p-5">
            <h2 className="text-xs font-semibold text-warn-700 uppercase tracking-wider mb-2">
              {t('arq.elParany')}
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed"
               dangerouslySetInnerHTML={{ __html: textRic(t(`arq.${arq.id}Parany`)) }} />
          </section>

          <section className="bg-ok-50 border border-ok-400 rounded-md p-5">
            <h2 className="text-xs font-semibold text-ok-700 uppercase tracking-wider mb-2">
              {t('arq.laPalanca')}
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed"
               dangerouslySetInnerHTML={{ __html: textRic(t(`arq.${arq.id}Palanca`)) }} />
          </section>
        </div>

        <div className="mt-6 flex items-center gap-4 flex-wrap">
          <button type="button" onClick={obreComAModel} disabled={creant}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-700 text-white
                       rounded-md text-sm font-semibold hover:bg-primary-800 disabled:opacity-50">
            {t('arq.obrir')}<ArrowRightIcon className="h-4 w-4" />
          </button>
          <span className="text-xs text-gray-500 max-w-md">{t('arq.obrirAjuda')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <Link to={tornaA}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <ArrowLeftIcon className="h-4 w-4" />
        {t(intern ? 'arq.tornaAlLlenc' : 'arq.tornaAModels')}
      </Link>

      <header className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{t('arq.titol')}</h1>
        <p className="text-gray-600 mt-2 max-w-3xl">{t('arq.intro')}</p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {ARQUETIPS.map((a) => {
          const n = comptaBucles(a);
          return (
            <button key={a.id} type="button" onClick={() => setObert(a.id)}
              className="text-left bg-white border-2 border-gray-200 rounded-md p-4
                         hover:border-primary-300 hover:shadow-sm transition-all">
              <div className="h-28 mb-3 overflow-hidden flex items-center justify-center">
                <Miniatura arquetip={a} nomDe={() => ''} alt={112} />
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold text-gray-900">{t(`arq.${a.id}Nom`)}</h2>
                <span className="text-[11px] text-gray-500 tabular-nums flex-shrink-0">
                  {t('arq.bucles', n)}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-1 leading-snug">{t(`arq.${a.id}Ganxo`)}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Com a `AjudaEtapa`: `**això**` passa a `<strong>` i la resta s'escapa.

